import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Printer, X } from 'lucide-react';
import axios from 'axios';

import usePrinterConfig from '../../hooks/usePrinterConfig';

const NODE = process.env.OPULENT_NODE_API_URL;
// Dismissal is persisted PER USER in the generic user_active_preferences store (no schema change),
// so once crossed it never shows again — on any station/browser. localStorage is only a cache of
// that flag so returning visits don't wait on (or even fire) the fetch.
const DISMISS_CONTEXT = 'printer-setup-banner';
const DISMISS_KEY = 'qz_banner_dismissed_v2';

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin: 0 0 16px;
  background: #FFF8E6;
  border: 1px solid #FFE2A8;
  border-radius: 8px;
  font-size: 13px;
  color: #7A5B00;
  .qz-b-ic { color: #C98A00; flex: none; }
  .qz-b-msg { flex: 1; }
  .qz-b-cta {
    border: 1px solid #C98A00;
    background: #fff;
    color: #8A5B00;
    font-weight: 600;
    font-size: 12px;
    border-radius: 6px;
    padding: 5px 12px;
    cursor: pointer;
  }
  .qz-b-cta:hover { background: #FFF1CC; }
  .qz-b-x {
    border: 0;
    background: transparent;
    cursor: pointer;
    color: #B08A3A;
    display: flex;
    padding: 4px;
  }
`;

/*
 * Layer-B nudge: a dismissible banner shown to printing roles (warehouse / seller+warehouse /
 * warehouse workers) until their label printers are set up. Drop it at the top of print-heavy
 * pages (e.g. FBA Inbound). It disappears the moment both printers are saved — usePrinterConfig is
 * reactive — and once crossed it is dismissed PERMANENTLY for that user (saved server-side, so it
 * stays gone across sessions, browsers, and stations). Nothing renders until the server flag has
 * been checked, so a dismissed banner never flashes in.
 */
export default function PrinterSetupBanner() {
  const navigate = useNavigate();
  const { currentUser: user } = useSelector((s) => s.auth || {});
  const { configured, featureEnabled } = usePrinterConfig();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  });
  // True once we know the server-side answer (or the local cache already told us).
  const [checked, setChecked] = useState(dismissed);

  const isPrintingRole = user?.role?.id === 3 || user?.role?.id === 5 || Boolean(user?.warehouse_manager_id);

  useEffect(() => {
    if (checked || !isPrintingRole || configured) return undefined;
    let alive = true;
    axios.get(`${NODE}/users/active-preference`, { params: { context_key: DISMISS_CONTEXT } })
      .then((res) => {
        if (!alive) return;
        if (res?.data?.data?.applied_filters?.dismissed === true) {
          setDismissed(true);
          try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* ignore */ }
        }
        setChecked(true);
      })
      .catch(() => { if (alive) setChecked(true); }); // can't check -> show; dismissing still saves
    return () => { alive = false; };
  }, [checked, isPrintingRole, configured]);

  // featureEnabled: the user switched QZ printing off on the Account page — no setup nudges.
  if (!featureEnabled || !isPrintingRole || configured || dismissed || !checked) return null;

  const dismiss = () => {
    // Hide immediately + cache locally, then persist server-side (fire-and-forget — a failed save
    // just means the banner may reappear on another device, never an error for the user).
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* ignore */ }
    axios.post(`${NODE}/users/active-preference`, {
      context_key: DISMISS_CONTEXT,
      applied_filters: { dismissed: true }
    }).catch(() => { /* ignore */ });
  };

  return (
    <Bar>
      <Printer size={16} className="qz-b-ic" />
      <span className="qz-b-msg">
        Printers aren’t set up. Connect QZ Tray once to print labels without the pop-up.
      </span>
      <button type="button" className="qz-b-cta" onClick={() => navigate('/account')}>Set up</button>
      <button type="button" className="qz-b-x" aria-label="Dismiss" onClick={dismiss}>
        <X size={15} />
      </button>
    </Bar>
  );
}
